import { mapGetters } from 'vuex'
import { sizeChartDelimiter } from '../../../store/centra-product'

export default {
  data () {
    return {
      selectedSize: []
    }
  },
  props: {
    // GA impression position, used in loops
    impressionPosition: {
      type: Number,
      default: 1
    },
    // GA impression list name, should represent where a product is displayed
    // and is entirely up to implementation and human readable (it can be anything)
    impressionListName: {
      type: String,
      default: 'Generic Product List'
    }
  },
  computed: {
    ...mapGetters({
      getProductById: 'centra-product/getProductById',
      market: 'frontend/market',
      pricelist: 'frontend/pricelist',
      currentLanguageCode: 'frontend/currentLanguageCode'
    }),

    /**
     * Don't access me directly, tyvm
     */
    product () {
      return this.transformProduct(this.getProductById(this.getProductId()))
    },

    /**
     * Used to determine if the size selector should be shown or not, to for
     * instance hide it if it's a one size product
     */
    showSizeSelector () {
      // Either there's more than one size option, or there's one size option
      // with more than one value
      if (this.product.sizeOptions.length > 1 || this.product.sizeOptions[0].length > 1) {
        return true
      }

      // Select the one and only size because there is no other sane thing to
      // do at this point. This doesn't feel right but if it's a guard we need
      // to somehow select the initial size and since it's async with an ID
      // we can't do it in the first data creator
      this.selectedSize[0] = this.sizeOptions[0][0].value
      return false
    },

    /**
     * True if the customer has selected all available size optios
     */
    allSizesSelected () {
      if (this.selectedSize.length !== this.sizeOptions.length) {
        return false
      }
      return this.selectedSize.filter(size => size !== '').length === this.selectedSize.length
    },

    /**
     * Always look at the right most item in order to figure out if it's in
     * stock or not, the other ones are mapped to labels
     */
    selectedSizeQty () {
      const itemId = this.selectedSizeValue
      if (itemId === false) {
        return false
      }
      const selectedItem = this.product.items[itemId]
      const qty = selectedItem.stockByMarket[this.market.market]
      return qty
    },

    /**
     * The right most selected value has the item ID
     */
    selectedSizeValue () {
      if (!this.allSizesSelected) {
        return false
      }
      return this.selectedSize[this.selectedSize.length - 1]
    }
  },

  /**
   * Initialize this array as an instance variable so it can be changed
   */
  beforeCreate () {
    this.postLoadCentraProductPromiseGetters = []
  },

  methods: {
    /**
     * The loader of a Centra product which is the main purpose of this mixin
     */
    fetchProduct () {
      return this.$store.dispatch('centra-product/lookupProducts', this.getProductId())
        .then(this.setInitialSizes)
        .then(this.getPostLoadCentraProductPromises)
    },

    /**
     * Use this method to inject dependencies to run after lookupProduct, so
     * content can be chain loaded within SSR. Things can include related
     * products, extra product content, etc etc. This should only be called after
     * a successful lookup
     *
     * This should be called instead of serverPrefetch for all depending components
     *
     * Getter must return Promise
     */
    postLoadCentraProduct (promiseGetter) {
      this.postLoadCentraProductPromiseGetters.push(promiseGetter)
    },

    /**
     * Builds and executes the array of promises when it's time to run them
     */
    getPostLoadCentraProductPromises () {
      const promises = []
      this.postLoadCentraProductPromiseGetters.forEach((promiseGetter) => {
        promises.push(promiseGetter())
      })
      return Promise.all(promises)
    },

    /**
     * Where the product ID is comes from is implementation specific and that's
     * why we require this to be overwritten
     */
    getProductId () {
      return this.id
    },

    /**
     * Returns the pricelist for the supplied product and market
     */
    getProductPricelist (product) {
      if (this.market && this.market.market) {
        return product.markets[this.market.market].pricesByPricelist[this.pricelist.pricelist]
      }
    },

    /**
     * Returns the price for the product in the current pricelist
     */
    getProductPrice (product, key = 'price') {
      const pricelist = this.getProductPricelist(product)
      if (pricelist) {
        return pricelist[key]
      } else {
        return ''
      }
    },

    /**
     * A super generic thing that should be rewritten with ugly client specific
     * label stuff
     */
    getSizeSelectLabel (index) {
      return 'Select Label ' + index
    },

    /**
     * Filters out non-unique values for itself and index > 0 - 1
     */
    getSizeOptionsForSelect (options, index) {
      if (this.sizeOptions.length === 1) {
        // Only one-dimensional so we just return the options as is
        return options
      } else {
        // If it's a left index in the options we just return a unique set with
        // the label as value, it doesn't really matter since we only use the
        // right-most value for adding to cart as it represents the final item id
        if (index < this.sizeOptions.length - 1) {
          const uniqueValues = [...new Set(options.map(item => item.label))]
          return uniqueValues.map((value) => { return { value, label: value } })
        }

        // If it's a higher index than 0 we have to filter out by items existing
        // in the options with index - 1 of this one, that has been selected.
        const selectedParent = this.selectedSize[index - 1]
        if (!selectedParent) {
          return [{
            label: 'Please Select ' + this.getSizeSelectLabel(index - 1),
            value: ''
          }]
        }

        const parentOptionValues = this.sizeOptions[index - 1].filter(option => option.label === selectedParent)
          .map(option => option.value)
        return options.filter(option => parentOptionValues.includes(option.value))
      }
    },

    /**
     * Implement this to keep track of sizes when switching between colors
     */
    setInitialSizes () {
    },

    /**
     * Needed by the size selector dropdown builder in order to split sizes
     * and lengths and so on
     */
    getSizeChartDelimiter () {
      return this.$config.sizeChartDelimiter ? this.$config.sizeChartDelimiter : sizeChartDelimiter
    },

    /**
     * Adds the selected item to cart, and tracks google analytics and similar
     * things if they are also added to the project
     */
    submitAddToCart () {
      if (this.allSizesSelected) {
        this.$store.dispatch('centra-cart/addItem', this.selectedSizeValue).then((res) => {
          const item = res.items.find(item => item.line === res.line)
          if (this.GA_trackAddToCart) {
            this.GA_trackAddToCart(item)
          }
          if (this.FB_trackAddToCart) {
            this.FB_trackAddToCart(item)
          }
        })
      }
    }

  }
}
